import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { decodeToken, isExpired } from 'react-jwt'
import AuthContext from '../contexts/AuthContext'

type AuthenticationData = {
  authToken: string
  initializing: boolean
  authUser: {
    id: string
    department_id: string
    email: string
    first_name: string
    last_name: string
    branch: string
    created_at?: string
    exp?: number
    last_signed_in?: string
    role_id: number
    site_admin: boolean
  }
}
type AuthContextType = {
  authUser: AuthenticationData['authUser']
  authToken: AuthenticationData['authToken']
  initializing: AuthenticationData['initializing']
  signIn?: (token: string) => void
  signOut?: () => void
}

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authToken, setAuthToken] = useState(
    localStorage.getItem('jwtToken') || '',
  )
  const [authState, setAuthState] = useState<AuthContextType>({
    authUser: {
      id: '',
      department_id: '',
      email: '',
      first_name: '',
      last_name: '',
      branch: '',
      role_id: 0,
      site_admin: false,
    },
    authToken: '',
    initializing: true,
  })

  const signIn = useCallback(
    (token: string) => {
      if (token === authToken) return
      localStorage.setItem('jwtToken', token)
      setAuthToken(token)
    },
    [authToken],
  )

  const signOut = useCallback(() => {
    localStorage.removeItem('jwtToken')
    ;[
      'reports_filter_financial_year',
      'reports_filter_department',
      'reports_filter_branch',
      'reports_filter_period',
      'reports_filter_paid_from',
      'reports_filter_international',
      'reports_filter_search',
      'reports_pagination',
    ].forEach((key) => sessionStorage.removeItem(key))
    setAuthToken('') // Consistency with authState
    setAuthState({
      authUser: {
        id: '',
        department_id: '',
        email: '',
        first_name: '',
        last_name: '',
        branch: '',
        role_id: 0,
        site_admin: false,
      },
      authToken: '',
      initializing: false,
    })
  }, [])

  useEffect(() => {
    const authUser: any = decodeToken(authToken)
    if (authUser) {
      delete authUser.password
    }
    const isAuthTokenExpired = isExpired(authToken)
    if (isAuthTokenExpired) {
      signOut()
      return
    }
    setAuthState({ authUser, authToken, initializing: false })
  }, [authToken, signOut])

  const contextValue = useMemo(
    () => ({ authState, signIn, signOut }),
    [authState, signIn, signOut],
  )

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuthentication = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthProvider
